import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Calendar, ChevronDown, ChevronUp, Plus, AlertTriangle, Image, Edit2, Trash2, Bell, BellOff, MessageSquare, CalendarPlus, ExternalLink, ShieldCheck, ShieldX, ShieldQuestion } from 'lucide-react';
import AddQualificationModal from './AddQualificationModal';
import EditPermitModal from './EditPermitModal';
import './PermitCard.css';

const SCHEDULING_URL = 'https://app.acuityscheduling.com/schedule/a5dbb23d';
// DCA License Verification URL
const DCA_VERIFY_URL = 'https://search.dca.ca.gov/';

// BSIS License Type codes for DCA search
const BSIS_LICENSE_TYPES = {
  firearms: '193',      // Exposed Firearm Permit
  guard_card: '192',    // Security Guard
  baton: '194',         // Baton Permit
  bsis_ppo: '195',      // Private Patrol Operator
  bsis_qm: '404',       // Private Patrol Qualified Manager
  bsis_pi: '213',       // Private Investigator
  bsis_locksmith: '207', // Locksmith Employee
  bsis_alarm: '203',    // Alarm Company Employee
};

const PermitCard = ({ permit, onUpdate, isCompanyAccount = false, isEmployee = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [qualifications, setQualifications] = useState([]);
  const [loadingQuals, setLoadingQuals] = useState(false);
  const [showAddQual, setShowAddQual] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(permit.tracking_enabled !== false);
  const [togglingTracking, setTogglingTracking] = useState(false);
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);
  const [changeRequestForm, setChangeRequestForm] = useState({ request_type: 'edit', description: '' });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calendarEmail, setCalendarEmail] = useState('');
  const [calendarDate, setCalendarDate] = useState('');
  // BSIS Verification state
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Check if this is a BSIS permit type
  const isBSISPermit = () => {
    return Object.keys(BSIS_LICENSE_TYPES).includes(permit.permit_type);
  };

  // Open DCA verification site with pre-filled data
  const openDCAVerification = () => {
    // Open DCA search page - user will need to select board and enter license number
    // We'll provide instructions in the modal
    window.open(DCA_VERIFY_URL, '_blank');
  };

  // Update verification status
  const handleVerificationUpdate = async (status) => {
    setVerifying(true);
    try {
      await axiosInstance.post(`/permits/${permit.id}/verify`, { status });
      toast.success(status === 'verified' 
        ? 'Permit verified successfully!' 
        : 'Verification marked as failed. Please contact admin for assistance.');
      setShowVerifyModal(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update verification status');
    } finally {
      setVerifying(false);
    }
  };

  // Initialize calendar date when modal opens
  const openCalendarModal = () => {
    const defaultDate = getDefaultCalendarDate();
    setCalendarDate(defaultDate.toISOString().split('T')[0]);
    setShowCalendarModal(true);
  };

  useEffect(() => {
    if (expanded && qualifications.length === 0) {
      fetchQualifications();
    }
  }, [expanded]);

  const fetchQualifications = async () => {
    setLoadingQuals(true);
    try {
      const response = await axiosInstance.get(`/permits/${permit.id}/qualifications`);
      setQualifications(response.data);
    } catch (error) {
      toast.error('Failed to load qualifications');
    } finally {
      setLoadingQuals(false);
    }
  };

  const handleCompleteQualification = async (qualId) => {
    try {
      await axiosInstance.post(`/qualifications/${qualId}/complete`);
      toast.success('Qualification marked as complete');
      fetchQualifications();
      onUpdate();
    } catch (error) {
      toast.error('Failed to update qualification');
    }
  };

  const handleTestReminder = async () => {
    try {
      const response = await axiosInstance.post(`/test/send-reminder/${permit.id}`);
      toast.success(
        `Test reminders sent!\n• Client: ${response.data.client_email}\n• Training Center: ${response.data.training_center_email}`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test reminder');
    }
  };

  const handleQualificationAdded = () => {
    fetchQualifications();
    setShowAddQual(false);
    onUpdate();
    toast.success('Qualification added successfully');
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    onUpdate();
  };

  const handleDeletePermit = async () => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete permit ${permit.permit_number}? This will also delete all associated qualifications. This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeleting(true);
    try {
      await axiosInstance.delete(`/permits/${permit.id}`);
      toast.success('Permit deleted successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete permit');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleTracking = async () => {
    setTogglingTracking(true);
    try {
      const response = await axiosInstance.patch(`/permits/${permit.id}/tracking`);
      setTrackingEnabled(response.data.tracking_enabled);
      toast.success(response.data.message);
      onUpdate();
    } catch (error) {
      toast.error('Failed to toggle tracking');
    } finally {
      setTogglingTracking(false);
    }
  };

  const handleSubmitChangeRequest = async (e) => {
    e.preventDefault();
    if (!changeRequestForm.description.trim()) {
      toast.error('Please describe the changes you need');
      return;
    }
    
    setSubmittingRequest(true);
    try {
      await axiosInstance.post('/employee/request-change', {
        permit_id: permit.id,
        request_type: changeRequestForm.request_type,
        description: changeRequestForm.description
      });
      toast.success('Change request submitted! Your company admin will be notified.');
      setShowChangeRequestModal(false);
      setChangeRequestForm({ request_type: 'edit', description: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit change request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Check if permit type is BSIS or CCW (shows scheduling link)
  const isBSISOrCCW = () => {
    const bsisTypes = ['firearms', 'guard_card', 'baton', 'bsis_ppo', 'bsis_qm', 'bsis_pi', 'bsis_locksmith', 'bsis_alarm'];
    const ccwTypes = ['ccw_ca', 'ccw_out_of_state', 'hr218'];
    return bsisTypes.includes(permit.permit_type) || ccwTypes.includes(permit.permit_type);
  };

  // Get the default date for calendar (first available date)
  const getDefaultCalendarDate = () => {
    // For firearms permits, use the FIRST day of qualification window (not the last)
    if (permit.permit_type === 'firearms' && permit.next_qual_window_start) {
      return new Date(permit.next_qual_window_start);
    }
    // For other permits, use renewal start date (90 days before expiration)
    if (permit.expiration_date) {
      const expDate = new Date(permit.expiration_date);
      const renewalDate = new Date(expDate);
      renewalDate.setDate(renewalDate.getDate() - 90);
      return renewalDate > new Date() ? renewalDate : expDate;
    }
    return new Date();
  };

  // Get the next important date for calendar (qualification or expiration)
  const getNextCalendarEvent = (customDate = null) => {
    const eventDate = customDate || getDefaultCalendarDate();
    
    // For firearms permits
    if (permit.permit_type === 'firearms') {
      return {
        title: `BSIS Firearms Qualification - Permit #${permit.permit_number}`,
        date: eventDate,
        description: `Your BSIS Exposed Firearm Permit qualification is due. Schedule your class at Southwest Training Center.\n\nPermit #: ${permit.permit_number}\n\nSchedule: ${SCHEDULING_URL}`
      };
    }
    // For other permits
    return {
      title: `${getPermitTypeName(permit.permit_type, permit.custom_permit_name)} Renewal Reminder`,
      date: eventDate,
      description: `Your permit is expiring on ${formatDate(permit.expiration_date)}. Schedule your renewal class at Southwest Training Center.\n\nPermit #: ${permit.permit_number}\n\nSchedule: ${SCHEDULING_URL}`
    };
  };

  // Generate Google Calendar URL
  const generateGoogleCalendarUrl = (event) => {
    const startDate = event.date.toISOString().replace(/-|:|\.\d+/g, '').slice(0, 8);
    const endDate = startDate; // All-day event
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      dates: `${startDate}/${endDate}`,
      details: event.description,
      location: 'Southwest Training Center, 10285 Glenoaks Blvd #6, Pacoima, CA 91331'
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Generate Outlook Calendar URL
  const generateOutlookCalendarUrl = (event) => {
    const startDate = event.date.toISOString().split('T')[0];
    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject: event.title,
      startdt: startDate,
      enddt: startDate,
      body: event.description,
      location: 'Southwest Training Center, 10285 Glenoaks Blvd #6, Pacoima, CA 91331'
    });
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
  };

  // Generate ICS file content for Apple Calendar / Download
  const generateICSContent = (event) => {
    const formatICSDate = (date) => {
      return date.toISOString().replace(/-|:|\.\d+/g, '').slice(0, 8);
    };
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Southwest Training Center//Permit Tracker//EN',
      'BEGIN:VEVENT',
      `DTSTART;VALUE=DATE:${formatICSDate(event.date)}`,
      `DTEND;VALUE=DATE:${formatICSDate(event.date)}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}`,
      'LOCATION:Southwest Training Center, 10285 Glenoaks Blvd #6, Pacoima, CA 91331',
      `UID:${permit.id}-${Date.now()}@stcpermittracker.com`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    
    return icsContent;
  };

  // Download ICS file
  const downloadICSFile = (event) => {
    const icsContent = generateICSContent(event);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `permit-reminder-${permit.permit_number}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Calendar file downloaded! Open it to add to your calendar.');
  };

  // Handle calendar add
  const handleAddToCalendar = (calendarType) => {
    // Use custom date from input if provided
    const customDate = calendarDate ? new Date(calendarDate) : null;
    const event = getNextCalendarEvent(customDate);
    
    if (!event) {
      toast.error('No date selected for calendar');
      return;
    }

    if (calendarType === 'google') {
      window.open(generateGoogleCalendarUrl(event), '_blank');
      toast.success('Opening Google Calendar...');
    } else if (calendarType === 'outlook') {
      window.open(generateOutlookCalendarUrl(event), '_blank');
      toast.success('Opening Outlook Calendar...');
    } else if (calendarType === 'apple') {
      downloadICSFile(event);
    }
    
    setShowCalendarModal(false);
    setCalendarEmail('');
    setCalendarDate('');
  };

  const getStatusClass = (status) => {
    return `status-badge status-${status.replace('_', '-')}`;
  };

  const getPermitTypeName = (type, customName) => {
    if (type === 'other' && customName) {
      return customName;
    }
    const names = {
      // BSIS Permits
      firearms: 'BSIS Exposed Firearm Permit',
      guard_card: 'BSIS Guard Card',
      baton: 'BSIS Baton Permit',
      bsis_ppo: 'BSIS PPO License',
      bsis_qm: 'BSIS QM License',
      bsis_pi: 'BSIS Private Investigator License',
      bsis_locksmith: 'BSIS Locksmith License',
      bsis_alarm: 'BSIS Alarm Company License',
      // CCW Permits
      ccw_ca: 'California CCW Permit',
      ccw_out_of_state: 'Out of State CCW Permit',
      hr218: 'HR218 LEOSA Permit',
      // Professional Permits
      doj_coe: 'DOJ COE License (Renews Yearly)',
      doj_firearms_instructor: 'CA DOJ Firearms Instructor License',
      doj_ccw_instructor: 'CA DOJ CCW Instructor License',
      first_aid: 'First Aid License',
      cpr: 'CPR License',
      tecc: 'TECC License',
      axon_taser_instructor: 'AXON TASER Instructor License',
      sabre_oc_instructor: 'SABRE OC Instructor License',
      nra_instructor: 'NRA Instructor License',
      uscca_instructor: 'USCCA Instructor License',
      crpa_instructor: 'CRPA Instructor License',
      // Personal Permits
      drivers_license: 'Drivers License',
      passport: 'Passport',
      vehicle_insurance: 'Vehicle Insurance',
      // Legacy support
      ccw: 'CCW Permit',
      private_investigator: 'Private Investigator License',
      locksmith: 'Locksmith License',
      alarm_installer: 'Alarm Operator/Installer',
      // Custom
      other: customName || 'Custom Permit',
    };
    return names[type] || type;
  };

  return (
    <>
      <div className={`permit-card ${!trackingEnabled ? 'tracking-disabled' : ''}`} data-testid={`permit-card-${permit.id}`}>
        <div className="permit-card-header">
          <div className="permit-main-info">
            <div className="permit-number-section">
              <span className="permit-type-label">{getPermitTypeName(permit.permit_type, permit.custom_permit_name)}</span>
              {permit.employee_name && (
                <span className="employee-name">{permit.employee_name}</span>
              )}
              <div className="permit-number-row">
                <span className="permit-number mono" data-testid="permit-number">
                  {permit.permit_number}
                </span>
                {/* BSIS Verification Badge & Button */}
                {isBSISPermit() && (
                  <div className="verification-section">
                    {permit.verification_status === 'verified' ? (
                      <span className="verification-badge verified" title={`Verified by ${permit.verified_by} on ${permit.verification_date ? new Date(permit.verification_date).toLocaleDateString() : 'N/A'}`}>
                        <ShieldCheck size={14} />
                        VERIFIED
                      </span>
                    ) : permit.verification_status === 'failed' ? (
                      <span className="verification-badge failed" title="Verification failed - contact admin">
                        <ShieldX size={14} />
                        FAILED
                      </span>
                    ) : (
                      <button 
                        className="verify-btn"
                        onClick={() => setShowVerifyModal(true)}
                        title="Verify this permit with BSIS"
                      >
                        <ShieldQuestion size={14} />
                        VERIFY
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="permit-header-right">
              {/* Tracking Toggle for Company Accounts */}
              {isCompanyAccount && (
                <button
                  className={`tracking-toggle ${trackingEnabled ? 'enabled' : 'disabled'}`}
                  onClick={handleToggleTracking}
                  disabled={togglingTracking}
                  title={trackingEnabled ? 'Tracking enabled - click to disable' : 'Tracking disabled - click to enable'}
                >
                  {trackingEnabled ? <Bell size={16} /> : <BellOff size={16} />}
                  <span className="tracking-label">{trackingEnabled ? 'Tracking' : 'No Tracking'}</span>
                </button>
              )}
              {permit.permit_image && (
                <button
                  className="view-image-btn"
                  onClick={() => setShowImage(true)}
                  title="View permit photo"
                >
                  <Image size={16} />
                </button>
              )}
              <span className={getStatusClass(permit.status)} data-testid="permit-status">
                {permit.status.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>

        <div className="permit-card-body">
          <div className="permit-dates">
            <div className="date-item">
              <span className="date-label">Issued</span>
              <span className="date-value mono">{formatDate(permit.issue_date)}</span>
            </div>
            <div className="date-divider"></div>
            <div className="date-item">
              <span className="date-label">Expires</span>
              <span className="date-value mono">{formatDate(permit.expiration_date)}</span>
            </div>
          </div>

          {/* Renewal Start Date Notice */}
          {permit.renewal_start_date && (
            <div className="renewal-notice" data-testid="renewal-notice">
              <AlertTriangle size={16} />
              <span>
                <strong>YOU SHOULD START RENEWAL AROUND:</strong>{' '}
                <span className="mono">{formatDate(permit.renewal_start_date)}</span>
              </span>
            </div>
          )}

          {/* Next Qualification Window for Firearms */}
          {permit.permit_type === 'firearms' && permit.next_qual_window_start && permit.next_qual_window_end && (
            <div className="next-qual-window">
              {/* Critical Warning Banner */}
              <div className="firearms-critical-warning">
                <AlertTriangle size={18} />
                <span>BSIS EXPOSED FIREARM PERMITS CAN NOT GO PAST THE EXPIRATION DATE. THERE IS NO GRACE PERIOD AND YOU WILL NEED TO START A BRAND NEW APPLICATION IF THE EXPIRATION DATE IS MISSED.</span>
              </div>
              
              <div className="window-header">
                <Calendar size={16} />
                <span className="window-title">NEXT QUALIFICATION WINDOW</span>
              </div>
              <div className="window-dates">
                <span className="window-label">Opens:</span>
                <span className="mono">{formatDate(permit.next_qual_window_start)}</span>
                <span className="window-separator">→</span>
                <span className="window-label">Closes:</span>
                <span className="mono deadline-date">{formatDate(permit.next_qual_window_end)}</span>
              </div>
              
              {/* Deadline Warning */}
              <div className="deadline-warning">
                <AlertTriangle size={14} />
                <span>⚠️ CAN NOT BE PAST THIS DATE</span>
              </div>
              
              {permit.next_qual_recommended && (
                <div className="recommended-date">
                  <span className="recommended-label">⭐ RECOMMENDED:</span>
                  <span className="mono recommended-value">{formatDate(permit.next_qual_recommended)}</span>
                </div>
              )}
              <p className="window-hint">
                <strong>Tip:</strong> Schedule your qualification during the 5th month. This gives you a 1-month buffer on each side in case of scheduling conflicts.
              </p>
              <p className="window-note">
                {!permit.qual_1?.completed 
                  ? "First qualification: 4-5 month window from issue date" 
                  : "Subsequent qualifications: 4-6 month window from last qualification"}
              </p>
            </div>
          )}

          {/* Schedule Class Link for BSIS and CCW permits */}
          {isBSISOrCCW() && (
            <div className="schedule-class-section">
              <a 
                href={SCHEDULING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="schedule-class-link"
              >
                <ExternalLink size={16} />
                CLICK HERE TO SCHEDULE YOUR NEXT CLASS
              </a>
              <button
                className="add-to-calendar-btn"
                onClick={openCalendarModal}
                title="Add reminder to your calendar"
              >
                <CalendarPlus size={16} />
                ADD TO YOUR CALENDAR
              </button>
            </div>
          )}
        </div>

        {/* Footer with Edit button - shown for all permits */}
        <div className="permit-card-footer">
          {isEmployee ? (
            <button
              className="btn-outline request-change-btn"
              onClick={() => setShowChangeRequestModal(true)}
              data-testid="request-change-btn"
            >
              <MessageSquare size={16} />
              Request Change
            </button>
          ) : (
            <>
              <button
                className="btn-outline edit-permit-btn"
                onClick={() => setShowEditModal(true)}
                data-testid="edit-permit-btn"
              >
                <Edit2 size={16} />
                Edit Permit
              </button>

              <button
                className="btn-outline delete-permit-btn"
                onClick={handleDeletePermit}
                disabled={deleting}
                data-testid="delete-permit-btn"
              >
                <Trash2 size={16} />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </>
          )}

          {permit.permit_type === 'firearms' && (
            <>
              <button
                className="expand-btn"
                onClick={() => setExpanded(!expanded)}
                data-testid="expand-qualifications-btn"
              >
                {expanded ? (
                  <>
                    <ChevronUp size={18} />
                    Hide Qualifications
                  </>
                ) : (
                  <>
                    <ChevronDown size={18} />
                    View All Qualifications
                  </>
                )}
              </button>

              {permit.next_qualification && !isEmployee && (
                <button
                  className="btn-outline test-reminder-btn"
                  onClick={handleTestReminder}
                  data-testid="test-reminder-btn"
                >
                  📧 Test Reminder
                </button>
              )}
            </>
          )}
        </div>

        {expanded && permit.permit_type === 'firearms' && (
          <div className="qualifications-section" data-testid="qualifications-section">
            <div className="qualifications-header">
              <h4>Qualifications</h4>
              <button
                className="btn-outline add-qual-btn"
                onClick={() => setShowAddQual(true)}
                data-testid="add-qual-btn"
              >
                <Plus size={16} />
                Add Past Qualification
              </button>
            </div>
            
            {loadingQuals ? (
              <div className="qualifications-loading">Loading...</div>
            ) : (
              <div className="qualifications-list">
                {qualifications.map((qual) => (
                  <div
                    key={qual.id}
                    className="qualification-item"
                    data-testid={`qualification-${qual.id}`}
                  >
                    <div className="qual-info">
                      <span className="qual-name">{qual.qualification_name}</span>
                      {qual.window_start && qual.window_end ? (
                        <div className="qual-window">
                          <span className="qual-window-label">Window:</span>
                          <span className="qual-window-dates mono">
                            {formatDate(qual.window_start)} — {formatDate(qual.window_end)}
                          </span>
                        </div>
                      ) : (
                        <span className="qual-date mono">{formatDate(qual.due_date)}</span>
                      )}
                      <span className={getStatusClass(qual.status)}>
                        {qual.status === 'in_window' ? 'IN WINDOW' : 
                         qual.status === 'window_approaching' ? 'WINDOW SOON' :
                         qual.status.replace('_', ' ')}
                      </span>
                    </div>
                    {!qual.completed && (qual.status === 'in_window' || qual.status === 'window_approaching' || qual.status === 'overdue') && (
                      <button
                        className="btn-outline qual-complete-btn"
                        onClick={() => handleCompleteQualification(qual.id)}
                        data-testid={`complete-qual-btn-${qual.id}`}
                      >
                        Mark Complete
                      </button>
                    )}
                    {qual.completed && qual.completed_date && (
                      <span className="completed-date">
                        Completed: {formatDate(qual.completed_date)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showAddQual && (
        <AddQualificationModal
          permitId={permit.id}
          onClose={() => setShowAddQual(false)}
          onSuccess={handleQualificationAdded}
        />
      )}

      {/* Edit Permit Modal */}
      {showEditModal && (
        <EditPermitModal
          permit={permit}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Permit Image Modal */}
      {showImage && permit.permit_image && (
        <div className="image-modal-overlay" onClick={() => setShowImage(false)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setShowImage(false)}>×</button>
            <img src={permit.permit_image} alt="Permit" className="permit-full-image" />
          </div>
        </div>
      )}

      {/* Change Request Modal for Employees */}
      {showChangeRequestModal && (
        <div className="modal-overlay" onClick={() => setShowChangeRequestModal(false)}>
          <div className="modal change-request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Request Change</h2>
              <button className="close-btn" onClick={() => setShowChangeRequestModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmitChangeRequest}>
                <div className="form-group">
                  <label>Permit</label>
                  <input 
                    type="text" 
                    value={`${getPermitTypeName(permit.permit_type, permit.custom_permit_name)} - #${permit.permit_number}`}
                    disabled 
                  />
                </div>
                <div className="form-group">
                  <label>Type of Change</label>
                  <select
                    value={changeRequestForm.request_type}
                    onChange={(e) => setChangeRequestForm({...changeRequestForm, request_type: e.target.value})}
                  >
                    <option value="edit">Edit Permit Details</option>
                    <option value="renewal">Request Renewal</option>
                    <option value="correction">Correct Information</option>
                    <option value="delete">Request Deletion</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Describe the changes needed *</label>
                  <textarea
                    value={changeRequestForm.description}
                    onChange={(e) => setChangeRequestForm({...changeRequestForm, description: e.target.value})}
                    placeholder="Please describe what changes you need made to this permit..."
                    rows={4}
                    required
                  />
                </div>
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn-outline" 
                    onClick={() => setShowChangeRequestModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={submittingRequest}
                  >
                    {submittingRequest ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add to Calendar Modal */}
      {showCalendarModal && (
        <div className="modal-overlay" onClick={() => setShowCalendarModal(false)}>
          <div className="modal calendar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <CalendarPlus size={20} />
                Add to Your Calendar
              </h2>
              <button className="close-btn" onClick={() => setShowCalendarModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="calendar-modal-info">
                Add a reminder for your next qualification or renewal to your calendar.
              </p>
              
              <div className="calendar-event-preview">
                <strong>Event:</strong> {permit.permit_type === 'firearms' 
                  ? `BSIS Firearms Qualification - Permit #${permit.permit_number}`
                  : `${getPermitTypeName(permit.permit_type, permit.custom_permit_name)} Renewal Reminder`}
                {permit.permit_type === 'firearms' && permit.next_qual_window_start && permit.next_qual_window_end && (
                  <div className="qualification-window-info">
                    <span className="window-badge">Qualification Window</span>
                    <span>{formatDate(permit.next_qual_window_start)} — {formatDate(permit.next_qual_window_end)}</span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Choose your preferred date *</label>
                <input 
                  type="date" 
                  value={calendarDate}
                  onChange={(e) => setCalendarDate(e.target.value)}
                  min={permit.next_qual_window_start ? permit.next_qual_window_start.split('T')[0] : undefined}
                  max={permit.next_qual_window_end ? permit.next_qual_window_end.split('T')[0] : undefined}
                />
                <p className="form-hint">
                  {permit.permit_type === 'firearms' && permit.next_qual_window_start
                    ? `Recommended: Schedule early in the window (${formatDate(permit.next_qual_window_start)})`
                    : 'Select a date to add to your calendar'}
                </p>
              </div>

              <div className="form-group">
                <label>Your Email (optional)</label>
                <input 
                  type="email" 
                  placeholder="your@email.com"
                  value={calendarEmail}
                  onChange={(e) => setCalendarEmail(e.target.value)}
                />
              </div>

              <div className="calendar-options">
                <button 
                  className="calendar-option google"
                  onClick={() => handleAddToCalendar('google')}
                  disabled={!calendarDate}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Google Calendar
                </button>
                
                <button 
                  className="calendar-option outlook"
                  onClick={() => handleAddToCalendar('outlook')}
                  disabled={!calendarDate}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.152-.352.229-.581.229h-8.547V6.58h8.547c.23 0 .423.077.58.229.16.152.239.345.239.578z" fill="#0364B8"/>
                    <path d="M24 7.387H14.634V6.58h8.547c.23 0 .423.077.58.229.16.152.239.345.239.578z" fill="#0078D4"/>
                    <path d="M14.634 6.58v11.09l-5.27 2.64V3.94l5.27 2.64z" fill="#28A8EA"/>
                    <path d="M9.364 3.94v16.37L0 17.67V6.58l9.364-2.64z" fill="#0078D4"/>
                    <path d="M5.739 9.387c-.9 0-1.703.42-2.154 1.088-.45.67-.52 1.516-.183 2.245.338.73 1.02 1.243 1.823 1.374.803.13 1.62-.155 2.19-.766.568-.61.793-1.46.6-2.275-.194-.816-.783-1.48-1.58-1.78-.23-.078-.464-.118-.696-.118v.232z" fill="#fff"/>
                  </svg>
                  Outlook Calendar
                </button>
                
                <button 
                  className="calendar-option apple"
                  onClick={() => handleAddToCalendar('apple')}
                  disabled={!calendarDate}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Apple / iPhone Calendar
                </button>
              </div>

              <p className="calendar-modal-hint">
                Select a date above, then choose your calendar to add the reminder.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* BSIS Verification Modal */}
      {showVerifyModal && (
        <div className="modal-overlay" onClick={() => setShowVerifyModal(false)}>
          <div className="modal verify-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header verify-header">
              <ShieldQuestion size={24} />
              <h2>Verify BSIS Permit</h2>
              <button className="close-btn" onClick={() => setShowVerifyModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="verify-permit-info">
                <p><strong>Permit Type:</strong> {getPermitTypeName(permit.permit_type, permit.custom_permit_name)}</p>
                <p><strong>Permit Number:</strong> <span className="mono">{permit.permit_number}</span></p>
              </div>

              <div className="verify-instructions">
                <h3>Step 1: Open BSIS Verification Site</h3>
                <p>Click the button below to open the California DCA license verification site in a new tab.</p>
                <button className="btn-open-dca" onClick={openDCAVerification}>
                  <ExternalLink size={16} />
                  Open DCA Verification Site
                </button>

                <h3>Step 2: Search for the License</h3>
                <ol>
                  <li>Under <strong>&quot;Boards and Bureaus&quot;</strong>, select:<br/>
                    <span className="highlight">&quot;Security and Investigative Services, Bureau of&quot;</span>
                  </li>
                  <li>Under <strong>&quot;License Type&quot;</strong>, select the appropriate type:<br/>
                    <span className="highlight">{getPermitTypeName(permit.permit_type, permit.custom_permit_name)}</span>
                  </li>
                  <li>Enter the <strong>License Number</strong>:<br/>
                    <span className="highlight mono">{permit.permit_number}</span>
                  </li>
                  <li>Click <strong>&quot;SEARCH&quot;</strong></li>
                </ol>

                <h3>Step 3: Confirm the Match</h3>
                <p>Check if the name on the DCA results matches the permit holder&apos;s name. Then select an option below:</p>
              </div>

              <div className="verify-actions">
                <button 
                  className="btn-verify-success"
                  onClick={() => handleVerificationUpdate('verified')}
                  disabled={verifying}
                >
                  <ShieldCheck size={18} />
                  {verifying ? 'Updating...' : 'Name Matches - VERIFIED'}
                </button>
                <button 
                  className="btn-verify-fail"
                  onClick={() => handleVerificationUpdate('failed')}
                  disabled={verifying}
                >
                  <ShieldX size={18} />
                  {verifying ? 'Updating...' : 'Name Does NOT Match'}
                </button>
              </div>

              <p className="verify-help-text">
                <AlertTriangle size={14} />
                If verification fails, please contact the admin at <strong>info@southwesttrainingcenter.org</strong> or call <strong>310-650-6896</strong> for assistance.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PermitCard;